import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { ReferenceSourceEnum, FedexShipmentSettingsDTO } from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { Callbacks } from '@api/apiRuntime';

/**
 * FedEx label settings: customer reference (applicationId or the pickup codes of all locations)
 * and whether multi-package shipments get all labels merged into one file.
 */
const FedexShipmentSettingsSection: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getFedexShipmentSettings, saveFedexShipmentSettings } = useContext(ApiContext);

  const [settings, setSettings] = useState<FedexShipmentSettingsDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFedexShipmentSettings(setSettings);
  }, []);

  const callbacks: Callbacks = {
    onValidationFailed: (response: any) => {
      const firstMessage = Array.isArray(response) ? response[0] : null;
      setError(firstMessage?.message ?? 'FedEx error');
      setSaving(false);
    },
    onError: (_, showStandardError) => {
      showStandardError();
      setSaving(false);
    },
  };

  const handleSave = () => {
    setError(null);
    setSaving(true);
    saveFedexShipmentSettings(
      settings,
      data => {
        setSettings(data);
        setSaving(false);
      },
      null,
      callbacks,
    );
  };

  if (settings == null) return <></>;

  const referenceOptions = [
    { value: ReferenceSourceEnum.APPLICATION_ID, label: tr('CompanyProfileFedex.shipmentSettings.reference.APPLICATION_ID', 'Expedition ID') },
    { value: ReferenceSourceEnum.PICKUP_CODES, label: tr('CompanyProfileFedex.shipmentSettings.reference.PICKUP_CODES', 'Pickup codes of all locations') },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-[700px]">
      <COHeadline variant={'h4'} title={tr('CompanyProfileFedex.shipmentSettings.title', 'Label settings')} />

      {error && <COInlineMessage variant={'error'}>{tr(error, error)}</COInlineMessage>}

      <Field label={tr('CompanyProfileFedex.shipmentSettings.referenceLabel', 'Customer reference')}>
        <Dropdown
          value={settings.referenceSource}
          options={referenceOptions}
          onChange={e => setSettings({ ...settings, referenceSource: e.value })}
          style={{ width: '100%' }}
        />
      </Field>
      {settings.referenceSource === ReferenceSourceEnum.PICKUP_CODES && (
        <span className="text-sm text-content-secondary">
          {tr(
            'CompanyProfileFedex.shipmentSettings.referenceHint',
            'Used only when every location has a pickup code, otherwise the expedition ID is sent. FedEx accepts max 40 characters.',
          )}
        </span>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Checkbox
            inputId="fedexMergeLabels"
            checked={settings.mergeLabels ?? false}
            onChange={e => setSettings({ ...settings, mergeLabels: e.checked })}
          />
          <label htmlFor="fedexMergeLabels">{tr('CompanyProfileFedex.shipmentSettings.mergeLabels', 'All labels in one file')}</label>
        </div>
        <span className="text-sm text-content-secondary">
          {tr(
            'CompanyProfileFedex.shipmentSettings.mergeLabelsHint',
            'Applies to multi-package shipments. FedEx returns the labels merged into one PDF.',
          )}
        </span>
      </div>

      <div>
        <RingilButton
          mode={'filled-rectangle'}
          label={tr('CompanyProfileFedex.shipmentSettings.save', 'Save')}
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        />
      </div>
    </div>
  );
};

export default FedexShipmentSettingsSection;
